import { Card, ListGroup, Spinner } from "react-bootstrap";
import { useLawsuitFinancialSummary } from "../../hooks/useLawsuitFinancialSummary";

function formatCurrency(value) {
  const amount = Number(value) || 0;
  return amount.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatDate(value) {
  if (!value) return "—";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [year, month, day] = value.slice(0, 10).split("-");
    return `${day}/${month}/${year}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("pt-BR");
}

function hasFinancialData(summary) {
  if (!summary) return false;
  const contracted = Number(summary.honorarios_contratados) || 0;
  const received = Number(summary.honorarios_recebidos) || 0;
  return contracted > 0 || received > 0 || Boolean(summary.proxima_parcela);
}

export default function FinancialSummaryCard({ lawsuitId }) {
  const { summary, isLoading, isFetching, isError } =
    useLawsuitFinancialSummary(lawsuitId);

  if (!lawsuitId) return null;

  if (isLoading || isFetching) {
    return (
      <Card className="mt-3">
        <Card.Body className="d-flex justify-content-center py-4">
          <Spinner animation="border" variant="warning" size="sm" />
        </Card.Body>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="mt-3">
        <Card.Body>
          <Card.Title as="h6" className="mb-2">
            Resumo financeiro
          </Card.Title>
          <p className="text-muted mb-0 small">
            Não foi possível carregar o resumo financeiro deste processo.
          </p>
        </Card.Body>
      </Card>
    );
  }

  if (!hasFinancialData(summary)) {
    return (
      <Card className="mt-3">
        <Card.Body>
          <Card.Title as="h6" className="mb-2">
            Resumo financeiro
          </Card.Title>
          <p className="text-muted mb-0 small">
            Nenhum honorário ou cobrança vinculada a este processo.
          </p>
        </Card.Body>
      </Card>
    );
  }

  const parcela = summary.proxima_parcela;

  return (
    <Card className="mt-3">
      <Card.Body>
        <Card.Title as="h6" className="mb-3">
          Resumo financeiro
        </Card.Title>
        <ListGroup variant="flush">
          <ListGroup.Item className="d-flex justify-content-between align-items-start px-0">
            <div className="fw-bold">Honorários contratados</div>
            <div>{formatCurrency(summary.honorarios_contratados)}</div>
          </ListGroup.Item>
          <ListGroup.Item className="d-flex justify-content-between align-items-start px-0">
            <div className="fw-bold">Honorários recebidos</div>
            <div>{formatCurrency(summary.honorarios_recebidos)}</div>
          </ListGroup.Item>
          <ListGroup.Item className="d-flex justify-content-between align-items-start px-0">
            <div className="fw-bold">Saldo</div>
            <div>{formatCurrency(summary.saldo)}</div>
          </ListGroup.Item>
          <ListGroup.Item className="px-0">
            <div className="fw-bold mb-1">Próxima parcela</div>
            {parcela ? (
              <div className="small">
                <div>
                  {formatCurrency(parcela.value)}
                  {parcela.status ? ` · ${parcela.status}` : ""}
                </div>
                <div className="text-muted">
                  Vencimento: {formatDate(parcela.due_date)}
                  {parcela.description ? ` · ${parcela.description}` : ""}
                </div>
              </div>
            ) : (
              <div className="text-muted small">Nenhuma parcela em aberto.</div>
            )}
          </ListGroup.Item>
        </ListGroup>
      </Card.Body>
    </Card>
  );
}
